import { LazyLoader } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { type FC, Suspense, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatches, useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { BpPage } from '../../components/BpPage/BpPage';
import {
  DesktopNavigationTabs,
  type DesktopNavigationTabsType,
} from '../../components/NavigationTabs/Desktop/DesktopNavigationTabs';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';

const GroupPagePageQueryDocument = graphql(`
  query GroupPagePageData($groupUuid: ID!) {
    groups(where: { uuid: $groupUuid }) {
      uuid
      __typename
      name
      nextcloudConnection {
        __typename
        edges {
          __typename
          properties {
            enabled
            directLink
            lastSync
          }
        }
      }
      organization {
        uuid
        __typename
      }
    }
  }
`);

export const GroupPage: FC = () => {
  const { t } = useTranslation();
  const { groupUuid } = useParams();
  const { pimAuthClaims } = useAuthClaims();

  const { cloudLicense, cloudLink } = useContext(OrganizationConfigContext);

  // hack hack hack for overview
  const matches = useMatches();
  const last = [...matches].pop();

  const [{ data }] = useQuery({
    query: GroupPagePageQueryDocument,
    variables: { groupUuid: groupUuid! },
    pause: groupUuid === undefined,
  });
  const perms = usePermissionChecker();
  const currentGroup = data?.groups[0];

  const cloudInfo = currentGroup?.nextcloudConnection?.edges.find((e) => e.properties?.enabled);

  const tabs: DesktopNavigationTabsType[] = [
    {
      title: t('common.overview'),
      path: `/groups/${groupUuid}`,
      active: last?.id === 'group-overview',
    },
    {
      title: t('workmaterials.title'),
      path: `/groups/${groupUuid}/workmaterials`,
    },
    {
      title: t('appointments.title'),
      path: `/groups/${groupUuid}/appointments`,
    },
    {
      title: t('persons.title', { count: 2 }),
      path: `/groups/${groupUuid}/persons`,
    },
    {
      title: 'Cloud', // TODO: external-link Icon
      path: `https://${cloudInfo?.properties.directLink ?? cloudLink}`,
      target: '_blank',
      disabled: !cloudLicense || cloudLink === '' || !cloudInfo,
    },
  ];

  return (
    <BpPage
      title={currentGroup?.name ?? t('groups.title')}
      isForbidden={
        !currentGroup ||
        !perms?.canViewGroup({
          uuid: groupUuid ?? '',
          organization: currentGroup?.organization,
        })
      }
      organization={pimAuthClaims.getOrganization()}
      hideTitleIfForbidden
    >
      <DesktopNavigationTabs className='mb-6' tabs={tabs} />
      <Suspense fallback={<LazyLoader embedded transparent forceHeight='45vh' />}>{<Outlet />}</Suspense>
    </BpPage>
  );
};
